'use client';

import React, { useRef, useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';
import ProductCard from '@/components/ProductCard';

export default function FeaturedProducts() {
  const { products } = useCommerce();
  const displayProducts = products.filter(p => p.isBestseller || p.isFestiveSpecial);
  const finalProducts = displayProducts.length > 0 ? displayProducts : products;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 15);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 15);
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const firstCard = el.querySelector('div');
    const cardWidth = firstCard ? firstCard.clientWidth : 240;
    const scrollAmount = (cardWidth + 20) * 3; // scroll 3 items at a time

    el.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  };

  if (finalProducts.length === 0) {
    return null;
  }

  return (
    <section id="bestsellers" className="w-full pt-14 sm:pt-16 md:pt-20 lg:pt-24 pb-16 sm:pb-20 md:pb-24 lg:pb-28 bg-white relative overflow-hidden">
      
      {/* 1. Centered Section Header with Subtitle Paragraph */}
      <div className="text-center max-w-3xl mx-auto px-4 mb-8 sm:mb-10 md:mb-12 space-y-2.5">
        <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl uppercase tracking-tight text-[#0F382A] leading-[1.18]">
          BESTSELLERS
        </h2>

        <p className="text-xs sm:text-sm md:text-base text-neutral-500 font-sans leading-relaxed max-w-2xl mx-auto">
          Handcrafted fresh daily with pure cold-pressed oils, rich cow ghee, and generational Chettinad recipes. Discover South India’s most cherished traditional savouries and sweets delivered with crisp guaranteed freshness.
        </p>

        <div className="pt-1">
          <Link
            href="/shop"
            className="inline-flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-[#950E09] hover:text-[#B90505] transition-colors"
          >
            <span>View All Bestsellers</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* 2. Full-Width Horizontal Edge-to-Edge Carousel */}
      <div className="relative w-full group/carousel">
        
        {/* Left Arrow Button */}
        <button
          type="button"
          onClick={() => handleScroll('left')}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
          className={`absolute left-2 sm:left-4 md:left-6 lg:left-8 top-[36%] -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 backdrop-blur-md shadow-2xl border border-neutral-200/90 flex items-center justify-center text-neutral-800 transition-all ${
            canScrollLeft
              ? 'hover:text-black hover:scale-110 active:scale-95 cursor-pointer opacity-100'
              : 'opacity-30 cursor-not-allowed pointer-events-none'
          }`}
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
        </button>

        {/* Right Arrow Button */}
        <button
          type="button"
          onClick={() => handleScroll('right')}
          disabled={!canScrollRight}
          aria-label="Scroll right"
          className={`absolute right-2 sm:right-4 md:right-6 lg:right-8 top-[36%] -translate-y-1/2 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/95 backdrop-blur-md shadow-2xl border border-neutral-200/90 flex items-center justify-center text-neutral-800 transition-all ${
            canScrollRight
              ? 'hover:text-black hover:scale-110 active:scale-95 cursor-pointer opacity-100'
              : 'opacity-30 cursor-not-allowed pointer-events-none'
          }`}
        >
          <ChevronRight className="w-5 h-5 stroke-[2.5]" />
        </button>

        {/* Horizontal Smooth Scrollable Cards Strip */}
        <div
          ref={scrollContainerRef}
          onScroll={updateScrollButtons}
          className="w-full flex items-start gap-4 sm:gap-5 overflow-x-auto scrollbar-none pb-6 pt-1 px-4 sm:px-6 md:px-8 lg:px-12 scroll-smooth"
        >
          {finalProducts.map((product, idx) => (
            <div
              key={product.id}
              className="w-[195px] sm:w-[215px] md:w-[235px] lg:w-[250px] xl:w-[260px] shrink-0"
            >
              <ProductCard
                product={product}
                index={idx}
              />
            </div>
          ))}
        </div>

      </div>

    </section>
  );
}
